/**
 * Public landing, the / route.
 *
 * A short front door, made to look like www.foundrytwin.com (dark iron, Archivo headings over IBM Plex Sans, one
 * heat-orange action): sign in or create an account, the Insights articles, and the legal pages. The full story of
 * the product lives on foundrytwin.com, so this page links there instead of repeating it. Its palette is fixed in
 * landing.css, not taken from the tenant theme, so it matches that site whatever mode the app is in.
 */

import { Link } from "react-router-dom";
import "@fontsource-variable/archivo/wdth.css";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/600.css";
import "./landing.css";
import { useTenantTheme } from "@/lib/tenant-theme";
import { ARTICLES } from "@/lib/insights/marketing/articles";

const SITE = "https://www.foundrytwin.com";

export function Landing() {
  const tenant = useTenantTheme();
  return (
    <div className="ftl">
      <header className="ftl-nav">
        <div className="ftl-wrap">
          <Link to="/" className="ftl-brand">
            {/* The name follows, so an org's logo is decorative here and the link reads as the name alone. */}
            {tenant.logoUrl ? <img src={tenant.logoUrl} alt="" /> : <TwinMark />}
            {tenant.productName}
          </Link>
          <nav className="ftl-links" aria-label="Main">
            <Link to="/insights">Insights</Link>
            <a href={SITE} className="ftl-wide">
              foundrytwin.com
            </a>
            <Link to="/login" className="ftl-btn ftl-btn-line ftl-btn-sm">
              Sign in
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="ftl-hero">
          <div className="ftl-wrap">
            <h1>Your plant, in one record.</h1>
            <p className="ftl-lead">
              Sign in to see today's brief, your scrap and what moved with it, and the actions in progress.
            </p>
            <div className="ftl-btns">
              <Link to="/login" className="ftl-btn ftl-btn-hot">
                Sign in
              </Link>
              <Link to="/signup" className="ftl-btn ftl-btn-line">
                Create an account
              </Link>
            </div>
            <p className="ftl-note">
              New to {tenant.productName}? <a href={SITE}>See what it does at foundrytwin.com</a>.
            </p>
          </div>
        </section>

        <section className="ftl-insights" aria-labelledby="ftl-insights">
          <div className="ftl-wrap">
            <div className="ftl-head">
              <h2 id="ftl-insights">Insights</h2>
              <Link to="/insights" className="ftl-more">
                All {ARTICLES.length} articles
              </Link>
            </div>
            <p className="ftl-sub">
              Plain writing for people who run plants: what a digital twin is, what scrap really costs, and how to start
              without replacing your controls.
            </p>
            <ul className="ftl-list">
              {ARTICLES.map((a) => (
                <li key={a.slug}>
                  <Link to={`/insights/${a.slug}`}>
                    <b>{a.title}</b>
                    <span>
                      {a.goal}, {a.readingMins} min read
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <footer className="ftl-foot">
        <div className="ftl-wrap">
          <p>Developed by Sahas Labs. Sales, distribution and testing equipment by Versatile Equipments.</p>
          <nav aria-label="Legal">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href={SITE}>foundrytwin.com</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/** The Foundry Twin hexagon in foundrytwin.com's colors: a heat-orange frame around the twin's node graph. */
function TwinMark() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 3.2 27 9.6 27 22.4 16 28.8 5 22.4 5 9.6Z" fill="none" stroke="#ff7a1a" strokeWidth="2.2" strokeLinejoin="round" />
      <g stroke="#e9edf2" strokeWidth="1.7" strokeLinecap="round">
        <line x1="16" y1="16" x2="16" y2="9.6" />
        <line x1="16" y1="16" x2="21.5" y2="19.2" />
        <line x1="16" y1="16" x2="10.5" y2="19.2" />
      </g>
      <g fill="#e9edf2">
        <circle cx="16" cy="16" r="2.7" />
        <circle cx="16" cy="9.4" r="1.9" />
        <circle cx="21.7" cy="19.3" r="1.9" />
        <circle cx="10.3" cy="19.3" r="1.9" />
      </g>
    </svg>
  );
}
